import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixFactory
} from "@@/shared/date-time/iso-and-unix";
import type { DeliveryMethodType } from "@@/shared/deliveries/delivery-methods";
import type { OrderGroupId } from "@@/shared/order-groups/order-group-id";
import type { ProviderId } from "@@/shared/providers/provider-id";
import type { NoSpecificData } from "@@/shared/socket-events/socket-event-base";
import type { CustomerId } from "@@/shared/users/customer-id";
import type { UserId } from "@@/shared/users/user-id";
import type { Order } from "../order";
import type { OrderId } from "../order-id";

type OrderSocketEventData<
    EventData extends Record<string, unknown> | NoSpecificData = NoSpecificData,
> = {
    providerId: ProviderId;
    providerSlug: string | undefined;
    orderGroupId: OrderGroupId;
    orderId: OrderId;
    customerId: UserId | CustomerId;
    deliveryMethodType: DeliveryMethodType;
    requestedDeliveryTime: IsoAndUnixTimestamp | "ASAP" | undefined;
    triggeredBy?: string;
} & (undefined extends EventData ? unknown : EventData);

const createOrderSocketEventData = <
    EventData extends Record<string, unknown> | NoSpecificData = NoSpecificData,
>(
    params: {
        order: Order;
        triggeredBy: string | UserId;
    } & (undefined extends EventData ? { data?: never } : { data: EventData }),
): OrderSocketEventData<EventData> => {
    const { order, triggeredBy } = params;
    const baseData: OrderSocketEventData = {
        customerId: order.customerId,
        deliveryMethodType: order.delivery.deliveryMethodType,
        orderGroupId: order.orderGroupId,
        orderId: order._id,
        providerId: order.providerId,
        providerSlug: order.providerSlug,
        triggeredBy,
        requestedDeliveryTime:
            order.delivery._type === "PICK_UP_DELIVERY"
                ? order.delivery.requestedDeliveryTime === "ASAP"
                    ? "ASAP"
                    : order.delivery.requestedDeliveryTime
                      ? isoAndUnixFactory(
                            // ALL NEW ORDERS SHOULD HAVE A REQUESTED DELIVERY TIME,
                            // ONLY OLD ORDERS SHOULD BE MISSING
                            order.delivery.requestedDeliveryTime,
                        )
                      : undefined
                : undefined,
    };
    return {
        ...(params.data ?? {}),
        ...baseData,
    } as OrderSocketEventData<EventData>;
};

export { createOrderSocketEventData };
export type { OrderSocketEventData };
